import { onMounted, ref, watch } from 'vue'
import type { Ref } from 'vue'
import { ApiClientError } from '@architeia/api-client'
import type { WorkspaceDetail, WorkspaceRef, WorkspaceSummary } from '@architeia/api-client'
import { getRuntimeConfig } from '@architeia/config'
import { useLatestRequest } from '@/composables/useLatestRequest'
import { useSecretOrgClient } from '@/composables/useSecretOrgClient'
import { messageRef, type MessageRef } from '@/i18n/messages'

// Mirrors the service's MAX_WORKSPACE_PANELS (services/secret-org content-ref.schema.ts). The web
// package cannot import it (the service never ships a runtime dependency on its source), so the
// server remains the authority — it answers PANEL_LIMIT_REACHED — and this only drives the UI copy.
export const MAX_WORKSPACE_PANELS = 12

export const workspacesEnabled = getRuntimeConfig().featureWorkspacesEnabled

export function parseWorkspaceRef(kind: WorkspaceRef['kind'], input: string): WorkspaceRef | null {
  const value = input.trim()
  if (kind === 'library-discussion') {
    const topicId = Number(value)
    return /^[1-9]\d*$/.test(value) && Number.isSafeInteger(topicId) ? { kind, topicId } : null
  }
  if (kind === 'performer-profile') {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
      ? { kind, profileId: value }
      : null
  }
  if (/^[A-Za-z0-9_-]{1,128}$/.test(value)) return { kind, shareId: value }
  try {
    const url = new URL(value)
    const hosts = new Set([
      'minglemage.com',
      'minglemage.dev.pinakes.de',
      'minglemage.staging.pinakes.de'
    ])
    const match = /^\/share\/([A-Za-z0-9_-]{1,128})\/?$/.exec(url.pathname)
    if (
      url.protocol !== 'https:' ||
      !hosts.has(url.host) ||
      url.username ||
      url.password ||
      url.search ||
      url.hash ||
      !match?.[1]
    )
      return null
    return { kind, shareId: match[1] }
  } catch {
    return null
  }
}

export function workspaceSourceUrl(value: string): string | null {
  if (
    /^\/(?:library\/[1-9]\d*-[a-z0-9-]+\/[1-9]\d*-[a-z0-9-]+|performers\/[a-f0-9-]{36})$/i.test(
      value
    )
  )
    return value
  try {
    const url = new URL(value)
    if (url.protocol === 'https:' && !url.username && !url.password) return url.toString()
  } catch {
    return null
  }
  return null
}

function saveMessage(error: unknown): MessageRef {
  if (error instanceof ApiClientError) {
    if (error.code === 'PANEL_ALREADY_PINNED') return messageRef('workspaces.errors.alreadyPinned')
    if (error.code === 'PANEL_LIMIT_REACHED')
      return messageRef('workspaces.errors.panelLimit', { max: MAX_WORKSPACE_PANELS })
    if (error.code === 'SOURCE_NOT_AVAILABLE')
      return messageRef('workspaces.errors.sourceUnavailable')
    if (error.statusCode === 409) return messageRef('workspaces.errors.conflict')
  }
  return messageRef('workspaces.errors.saveFailed')
}

export function useWorkspaceList(): {
  workspaces: Ref<WorkspaceSummary[]>
  state: Ref<'loading' | 'ready' | 'error'>
  loadingMore: Ref<boolean>
  nextCursor: Ref<string | null>
  saving: Ref<boolean>
  error: Ref<MessageRef | null>
  load: () => Promise<void>
  loadMore: () => Promise<void>
  create: (title: string, goal: string) => Promise<string | null>
} {
  const workspaces = ref<WorkspaceSummary[]>([])
  const state = ref<'loading' | 'ready' | 'error'>('loading')
  const nextCursor = ref<string | null>(null)
  const loadingMore = ref(false)
  const saving = ref(false)
  const error = ref<MessageRef | null>(null)
  const latest = useLatestRequest()
  const api = useSecretOrgClient()

  async function load(): Promise<void> {
    const token = latest.begin()
    state.value = 'loading'
    error.value = null
    try {
      const page = await api.listWorkspaces()
      if (!latest.isCurrent(token)) return
      workspaces.value = page.workspaces
      nextCursor.value = page.nextCursor
      state.value = 'ready'
    } catch {
      if (latest.isCurrent(token)) state.value = 'error'
    }
  }

  async function loadMore(): Promise<void> {
    if (!nextCursor.value || loadingMore.value) return
    const token = latest.current()
    loadingMore.value = true
    try {
      const page = await api.listWorkspaces(20, nextCursor.value)
      if (!latest.isCurrent(token)) return
      const existing = new Set(workspaces.value.map(workspace => workspace.id))
      workspaces.value.push(...page.workspaces.filter(workspace => !existing.has(workspace.id)))
      nextCursor.value = page.nextCursor
    } catch {
      if (latest.isCurrent(token)) error.value = messageRef('workspaces.errors.loadMoreFailed')
    } finally {
      loadingMore.value = false
    }
  }

  async function create(title: string, goal: string): Promise<string | null> {
    if (saving.value) return null
    saving.value = true
    error.value = null
    try {
      const workspace = await api.createWorkspace(title.trim(), goal.trim() || null)
      return workspace.path
    } catch (cause) {
      error.value = saveMessage(cause)
      return null
    } finally {
      saving.value = false
    }
  }

  onMounted(() => {
    void load()
  })
  return { workspaces, state, loadingMore, nextCursor, saving, error, load, loadMore, create }
}

export type SuggestionAddOutcome =
  'added' | 'duplicate' | 'unavailable' | 'limit' | 'conflict' | 'busy' | 'error'

/** The outcome an add-suggested failure maps to; anything unrecognised is a plain 'error'. */
function suggestionFailure(cause: unknown): SuggestionAddOutcome {
  if (cause instanceof ApiClientError) {
    if (cause.code === 'PANEL_ALREADY_PINNED') return 'duplicate'
    if (cause.code === 'SOURCE_NOT_AVAILABLE') return 'unavailable'
    if (cause.code === 'PANEL_LIMIT_REACHED') return 'limit'
    if (cause.statusCode === 409) return 'conflict'
  }
  return 'error'
}

export function useWorkspaceDetail(id: Ref<string>): {
  workspace: Ref<WorkspaceDetail | null>
  state: Ref<'loading' | 'ready' | 'missing' | 'error' | 'unsupported'>
  title: Ref<string>
  goal: Ref<string>
  pinKind: Ref<WorkspaceRef['kind']>
  sourceInput: Ref<string>
  saving: Ref<boolean>
  error: Ref<MessageRef | null>
  load: () => Promise<void>
  saveMetadata: () => Promise<void>
  pin: () => Promise<void>
  addSuggested: (ref: WorkspaceRef) => Promise<SuggestionAddOutcome>
  move: (index: number, direction: -1 | 1) => Promise<void>
  remove: (panelId: string) => Promise<void>
  deleteWorkspace: () => Promise<boolean>
} {
  const workspace = ref<WorkspaceDetail | null>(null)
  const state = ref<'loading' | 'ready' | 'missing' | 'error' | 'unsupported'>('loading')
  const title = ref('')
  const goal = ref('')
  const pinKind = ref<WorkspaceRef['kind']>('library-discussion')
  const sourceInput = ref('')
  const saving = ref(false)
  const error = ref<MessageRef | null>(null)
  const latest = useLatestRequest()
  const api = useSecretOrgClient()

  /** Loads the workspace; resolves true when this was still the latest request when it settled. */
  async function loadWorkspace(): Promise<boolean> {
    const token = latest.begin()
    const workspaceId = id.value
    if (!workspace.value) state.value = 'loading'
    try {
      const result = await api.getWorkspace(workspaceId)
      if (!latest.isCurrent(token)) return false
      workspace.value = result
      title.value = result.title
      goal.value = result.goal ?? ''
      state.value = 'ready'
      error.value = null
    } catch (cause) {
      if (!latest.isCurrent(token)) return false
      if (cause instanceof ApiClientError && cause.code === 'UNSUPPORTED_LAYOUT') {
        workspace.value = null
        state.value = 'unsupported'
      } else if (cause instanceof ApiClientError && cause.statusCode === 404) {
        workspace.value = null
        state.value = 'missing'
      } else state.value = 'error'
    }
    return true
  }

  async function load(): Promise<void> {
    await loadWorkspace()
  }

  async function mutate(
    action: (current: WorkspaceDetail) => Promise<WorkspaceDetail>
  ): Promise<void> {
    if (!workspace.value || saving.value) return
    const token = latest.current()
    const snapshot = workspace.value
    saving.value = true
    error.value = null
    try {
      const result = await action(snapshot)
      if (!latest.isCurrent(token)) return
      workspace.value = result
      state.value = 'ready'
    } catch (cause) {
      if (latest.isCurrent(token)) error.value = saveMessage(cause)
    } finally {
      if (latest.isCurrent(token)) saving.value = false
    }
  }

  async function saveMetadata(): Promise<void> {
    await mutate(async current => {
      const result = await api.updateWorkspace(
        current.id,
        title.value.trim(),
        goal.value.trim() || null,
        current.revision
      )
      return { ...current, ...result }
    })
  }

  async function pin(): Promise<void> {
    const ref = parseWorkspaceRef(pinKind.value, sourceInput.value)
    if (!ref) {
      error.value = messageRef('workspaces.errors.invalidSource')
      return
    }
    await mutate(current => api.addWorkspacePanel(current.id, ref, current.revision))
    if (!error.value) sourceInput.value = ''
  }

  async function addSuggested(ref: WorkspaceRef): Promise<SuggestionAddOutcome> {
    if (!workspace.value || saving.value) return 'busy'
    const token = latest.current()
    const snapshot = workspace.value
    saving.value = true
    let outcome: SuggestionAddOutcome = 'added'
    let result: WorkspaceDetail | undefined
    try {
      result = await api.addWorkspacePanel(snapshot.id, ref, snapshot.revision)
    } catch (cause) {
      outcome = suggestionFailure(cause)
    }
    // The workspace changed underneath the call (another id, a delete): whoever changed it owns
    // `saving` and the state, so this result is dropped.
    if (!latest.isCurrent(token)) return 'busy'
    if (result) {
      workspace.value = result
      state.value = 'ready'
    }
    // After a 409 the cached revision is stale, so `saving` stays true until the reload settles:
    // a second Add before then would go out with the stale revision and hit a second 409. If the
    // reload was itself superseded, whoever superseded it owns `saving`.
    if (outcome === 'conflict' && !(await loadWorkspace())) return outcome
    saving.value = false
    return outcome
  }

  async function move(index: number, direction: -1 | 1): Promise<void> {
    await mutate(current => {
      const panels = current.panels.map(panel => panel.panelId)
      const target = index + direction
      const source = panels[index]
      const other = panels[target]
      if (!source || !other) return Promise.resolve(current)
      panels[index] = other
      panels[target] = source
      return api.putWorkspaceLayout(current.id, panels, current.revision)
    })
  }

  async function remove(panelId: string): Promise<void> {
    await mutate(current =>
      api.putWorkspaceLayout(
        current.id,
        current.panels.filter(panel => panel.panelId !== panelId).map(panel => panel.panelId),
        current.revision
      )
    )
  }

  async function deleteWorkspace(): Promise<boolean> {
    if (!workspace.value || saving.value) return false
    const token = latest.current()
    saving.value = true
    error.value = null
    try {
      await api.deleteWorkspace(workspace.value.id)
      return latest.isCurrent(token)
    } catch (cause) {
      if (latest.isCurrent(token)) error.value = saveMessage(cause)
      return false
    } finally {
      if (latest.isCurrent(token)) saving.value = false
    }
  }

  watch(
    id,
    () => {
      latest.invalidate()
      workspace.value = null
      saving.value = false
      error.value = null
      sourceInput.value = ''
      void load()
    },
    { immediate: true }
  )
  return {
    workspace,
    state,
    title,
    goal,
    pinKind,
    sourceInput,
    saving,
    error,
    load,
    saveMetadata,
    pin,
    addSuggested,
    move,
    remove,
    deleteWorkspace
  }
}

export function useWorkspacePin(source: Ref<WorkspaceRef>): {
  workspaces: Ref<WorkspaceSummary[]>
  selectedId: Ref<string>
  state: Ref<'loading' | 'ready' | 'error'>
  saving: Ref<boolean>
  message: Ref<MessageRef | null>
  nextCursor: Ref<string | null>
  load: () => Promise<void>
  loadMore: () => Promise<void>
  pin: () => Promise<void>
} {
  const list = useWorkspaceList()
  const api = useSecretOrgClient()
  const selectedId = ref('')
  const saving = ref(false)
  const message = ref<MessageRef | null>(null)
  const latest = useLatestRequest()
  watch(list.error, error => {
    if (error) message.value = error
  })
  watch(
    () => JSON.stringify(source.value),
    () => {
      latest.invalidate()
      message.value = null
      saving.value = false
    }
  )
  watch(list.workspaces, workspaces => {
    if (!workspaces.some(workspace => workspace.id === selectedId.value))
      selectedId.value = workspaces[0]?.id ?? ''
  })

  async function pin(): Promise<void> {
    const workspace = list.workspaces.value.find(item => item.id === selectedId.value)
    if (!workspace || saving.value) return
    const token = latest.current()
    saving.value = true
    message.value = null
    try {
      const result = await api.addWorkspacePanel(workspace.id, source.value, workspace.revision)
      if (!latest.isCurrent(token)) return
      workspace.revision = result.revision
      message.value = messageRef('workspaces.pin.pinned')
    } catch (cause) {
      if (latest.isCurrent(token)) message.value = saveMessage(cause)
    } finally {
      if (latest.isCurrent(token)) saving.value = false
    }
  }

  return {
    workspaces: list.workspaces,
    selectedId,
    state: list.state,
    saving,
    message,
    nextCursor: list.nextCursor,
    load: list.load,
    loadMore: list.loadMore,
    pin
  }
}
